import { useState, useEffect } from 'react';
import Modal from '@/components/Modal';
import axios from 'axios';

const ProductsImportModal = ({ isOpen, onClose, onConfirm, restaurantId }) => {
    const [categories, setCategories] = useState([]);
    const [selectedProducts, setSelectedProducts] = useState({});
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchCategories = async () => {
            try {
                const response = await axios.get(`${import.meta.env.VITE_API_URL}/api/settings/categories/list`);
                if (response.status === 200) {
                    const categoriesWithProducts = await Promise.all(
                        response.data.map(async (category) => {
                            const productsResponse = await axios.get(
                                `${import.meta.env.VITE_API_URL}/api/restaurants/products/category/${category.id}?restaurantId=${restaurantId}`
                            );
                            return {
                                ...category,
                                products: productsResponse.data || []
                            };
                        })
                    );
                    setCategories(categoriesWithProducts.filter(cat => cat.products.length > 0));
                }
            } catch (error) {
                console.error('Erreur lors de la récupération des catégories:', error);
            } finally {
                setLoading(false);
            }
        };

        if (isOpen) {
            fetchCategories();
        }
    }, [isOpen, restaurantId]);

    const handleCategorySelect = (categoryId) => {
        const category = categories.find(cat => cat.id === categoryId);
        const productIds = category.products.map(product => product.product_id);
        
        setSelectedProducts(prev => {
            const newSelection = { ...prev };
            
            if (newSelection[categoryId]?.length === productIds.length) {
                delete newSelection[categoryId];
            } else {
                newSelection[categoryId] = productIds;
            }
            
            return newSelection;
        });
    };

    const handleProductSelect = (categoryId, productId) => {
        setSelectedProducts(prev => {
            const newSelection = { ...prev };
            
            if (!newSelection[categoryId]) {
                newSelection[categoryId] = [productId];
            } else {
                const index = newSelection[categoryId].indexOf(productId);
                if (index === -1) {
                    newSelection[categoryId] = [...newSelection[categoryId], productId];
                } else {
                    newSelection[categoryId] = newSelection[categoryId].filter(id => id !== productId);
                    if (newSelection[categoryId].length === 0) {
                        delete newSelection[categoryId];
                    }
                }
            }
            
            return newSelection;
        });
    };

    const isProductSelected = (categoryId, productId) => {
        return selectedProducts[categoryId]?.includes(productId);
    };

    const isCategorySelected = (categoryId) => {
        const category = categories.find(cat => cat.id === categoryId);
        return selectedProducts[categoryId]?.length === category?.products.length;
    };

    const handleConfirm = () => {
        console.log("Structure complète des produits:", categories);
        
        const selectedProductIds = Object.entries(selectedProducts).flatMap(([categoryId, productIds]) => {
            const category = categories.find(c => c.id === categoryId);
            if (!category) return [];
            
            return productIds.map(productId => {
                const product = category.products.find(p => p.product_id === productId);
                console.log("Produit sélectionné:", product);
                return product?.product_id;
            });
        }).filter(Boolean);
        
        console.log("IDs des produits sélectionnés:", selectedProductIds);
        onConfirm(selectedProductIds);
        onClose();
    };

    if (!isOpen) return null;

    return (
        <Modal
            title="Sélection des produits à importer"
            setShowModal={onClose}
            type="info"
            handleSubmit={handleConfirm}
        >
            {loading ? (
                <div className="flex justify-center items-center h-40">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                </div>
            ) : (
                <div className="max-h-[60vh] overflow-y-auto">
                    {categories.map((category) => (
                        <div key={category.id} className="mb-6">
                            <div className="flex items-center mb-2">
                                <input
                                    type="checkbox"
                                    id={`category-${category.id}`}
                                    checked={isCategorySelected(category.id)}
                                    onChange={() => handleCategorySelect(category.id)}
                                    className="h-4 w-4 text-primary focus:ring-primary/70 border-gray-300 rounded mr-2"
                                />
                                <label htmlFor={`category-${category.id}`} className="font-semibold">
                                    {category.name}
                                </label>
                            </div>
                            <div className="ml-6 space-y-2">
                                {category.products.map((product, index) => (
                                    <div key={product._product_id || index} className="flex items-center">
                                        <input
                                            type="checkbox"
                                            id={`product-${product.product_id}`}
                                            checked={isProductSelected(category.id, product.product_id)}
                                            onChange={() => handleProductSelect(category.id, product.product_id)}
                                            className="h-4 w-4 text-primary focus:ring-primary/70 border-gray-300 rounded mr-2"
                                        />
                                        <label htmlFor={`product-${product.product_id}`} className="flex items-center">
                                            {product.picture && (
                                                <img
                                                    src={product.picture}
                                                    alt={product.name}
                                                    className="w-8 h-8 object-cover rounded-full mr-2"
                                                />
                                            )}
                                            <span>{product.name}</span>
                                        </label>
                                    </div>
                                ))}
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </Modal>
    );
};

export default ProductsImportModal; 